import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AiService } from '../../services/ai.service';
import { DashboardService } from '../../services/dashboard.service';
import { ToastrService } from 'ngx-toastr';
import { formatMarkdown } from 'src/app/_services/markdown.util';

type ChatMessage = { role: string; content: string; saved?: boolean; saving?: boolean };

@Component({
  selector: 'tangram-ai-chat',
  templateUrl: './ai-chat.component.html',
  styleUrls: ['./ai-chat.component.scss']
})
export class AiChatComponent implements OnInit {
  @Input() pathwayId: string;
  @Input() videoId: string;
  @Input() isDashboard: boolean;
  @Input() subscriptionActive: boolean;
  @Output() chatOpenChange = new EventEmitter<boolean>();
  @Output() noteSaved = new EventEmitter<any>();

  @ViewChild('messagesContainer', { static: false }) messagesContainer: ElementRef;
  @ViewChild('messageInput', { static: false }) messageInput: ElementRef;

  // Per-provider message histories — each provider keeps its own conversation
  // so switching from Gemini to OpenAI (or vice versa) never clobbers what's
  // already there. `messages` is a live reference to the active provider's
  // array so the template can keep iterating `messages` without caring which
  // provider it belongs to.
  messagesByProvider: { gemini: Array<ChatMessage>; openai: Array<ChatMessage> } = { gemini: [], openai: [] };
  messages: Array<ChatMessage> = this.messagesByProvider.gemini;
  currentMessage: string = '';
  isLoading: boolean = false;
  isOpen: boolean = false;
  // When minimized the panel is hidden but messages are kept so the user can resume.
  isMinimized: boolean = false;
  activeProvider: 'gemini' | 'openai' = 'gemini';

  get providerLabel(): string {
    return this.activeProvider === 'openai' ? 'OpenAI' : 'Gemini';
  }

  constructor(
    private aiService: AiService,
    private dashboardService: DashboardService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {}

  openChat(provider: 'gemini' | 'openai') {
    // return; // TODO: Temporarily disabled for client demo
    if (!this.subscriptionActive) {
      this.toastr.warning('Active subscription required for AI features.');
      return;
    }
    this.activeProvider = provider;
    // Point `messages` at the provider's own history so any existing chat with
    // this provider is preserved across opens. The conversation is only ever
    // wiped by the explicit × close button below.
    this.messages = this.messagesByProvider[provider];
    this.isOpen = true;
    this.isMinimized = false;
    this.chatOpenChange.emit(true);
    this.scrollToBottom();
  }

  /** Reopen an already-started conversation that was minimized. */
  resumeChat() {
    this.isOpen = true;
    this.isMinimized = false;
    this.chatOpenChange.emit(true);
    this.scrollToBottom();
  }

  /** True when the given provider has a stashed conversation — drives the
   *  small dot indicator on its trigger button. Independent of `isMinimized`
   *  so the dot stays visible while the panel is open on the OTHER provider. */
  hasActiveChatFor(provider: 'gemini' | 'openai'): boolean {
    return this.messagesByProvider[provider].length > 0;
  }

  /** Click handler for the always-visible provider buttons. Always routes
   *  through `openChat`, which now re-attaches to the provider's existing
   *  history instead of resetting it — so switching providers never clears
   *  the other provider's chat. */
  onTriggerClick(provider: 'gemini' | 'openai') {
    if (this.isMinimized && this.activeProvider === provider && this.messages.length > 0) {
      this.resumeChat();
    } else {
      this.openChat(provider);
    }
  }

  toggleChat() {
    // return; // TODO: Temporarily disabled for client demo
    if (!this.subscriptionActive && !this.isOpen) {
      this.toastr.warning('Active subscription required for AI features.');
      return;
    }
    this.isOpen = !this.isOpen;
  }

  /** Hide the chat panel without discarding the conversation. */
  minimizeChat() {
    this.isOpen = false;
    this.isMinimized = true;
    this.chatOpenChange.emit(false);
  }

  /** Close the chat AND discard the active provider's conversation. The other
   *  provider's history is left alone — its dot will stay visible on its
   *  trigger button so the user can still resume it. */
  closeChat() {
    this.isOpen = false;
    this.isMinimized = false;
    this.messagesByProvider[this.activeProvider] = [];
    this.messages = this.messagesByProvider[this.activeProvider];
    this.chatOpenChange.emit(false);
  }

  /** Save an assistant response as a Private Note on the current tangram/media.
   *  The raw markdown is persisted (not stripped) along with `isMarkdown: true`
   *  so the notes list can render it with the same formatting used in the chat. */
  saveAsNote(msg: { role: string; content: string; saved?: boolean; saving?: boolean }) {
    if (!msg || msg.role !== 'assistant' || msg.saved || msg.saving) return;
    const text = (msg.content || '').trim();
    if (!text) return;
    const videoId = !this.isDashboard && this.videoId ? this.videoId : undefined;
    msg.saving = true;
    this.dashboardService.createNote(this.pathwayId, text, true /* isPrivate */, videoId, true /* isMarkdown */).subscribe(
      (res: any) => {
        msg.saving = false;
        if (res && res.success) {
          msg.saved = true;
          this.toastr.success('Saved to Private Notes.');
          // Let the parent dashboard refresh its notes list without re-fetching.
          this.noteSaved.emit(res.data);
        } else {
          this.toastr.error((res && res.message) || 'Failed to save note');
        }
      },
      (err: any) => {
        msg.saving = false;
        const m = (err && err.error && err.error.message) || 'Failed to save note';
        this.toastr.error(m);
      }
    );
  }

  onEnterKey(event: KeyboardEvent) {
    if (event.shiftKey) return; // Shift+Enter = new line
    event.preventDefault();
    this.sendMessage();
  }

  autoResize() {
    if (!this.messageInput || !this.messageInput.nativeElement) return;
    const el = this.messageInput.nativeElement;
    el.style.height = 'auto';
    const newHeight = Math.min(el.scrollHeight, 120);
    el.style.height = newHeight + 'px';
    el.style.overflowY = el.scrollHeight > 120 ? 'auto' : 'hidden';
  }

  sendMessage() {
    // return; // TODO: Temporarily disabled for client demo
    if (!this.currentMessage.trim() || this.isLoading) {
      return;
    }

    if (!this.subscriptionActive) {
      this.toastr.warning('Active subscription required for AI features.');
      return;
    }

    const userMessage = this.currentMessage.trim();
    this.currentMessage = '';
    setTimeout(() => {
      if (this.messageInput && this.messageInput.nativeElement) {
        this.messageInput.nativeElement.style.height = '38px';
        this.messageInput.nativeElement.style.overflowY = 'hidden';
      }
    }, 0);

    // Add user message to display immediately (optimistic UI)
    this.messages.push({ role: 'user', content: userMessage });
    this.scrollToBottom();

    this.isLoading = true;

    // Build request payload
    const payload: any = {
      message: userMessage,
      provider: this.activeProvider,
      messages: this.messages.slice(0, -1), // Send history without the current message (backend adds it)
    };

    if (this.isDashboard) {
      payload.pathwayId = this.pathwayId;
    } else {
      payload.videoId = this.videoId;
    }
    this.aiService.sendChatMessage(payload).subscribe(
      (res: any) => {
        this.isLoading = false;
        if (res.success && res.data) {
          this.messages.push({ role: 'assistant', content: res.data.response });
          this.scrollToBottom();
        }
      },
      (error: any) => {
        this.isLoading = false;
        this.messages.pop();
        if (error.error && error.error.requiresSubscription) {
          this.toastr.warning('Active subscription required for AI features.');
        } else {
          this.toastr.error('Failed to get response. Please try again.');
        }
      }
    );
  }

  formatMarkdown(text: string): string {
    return formatMarkdown(text);
  }

  private scrollToBottom() {
    setTimeout(() => {
      if (this.messagesContainer) {
        const el = this.messagesContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    }, 100);
  }
}
